import React, { useMemo } from 'react';
import {
  Grid,
  Typography,
  Theme,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import clsx from 'clsx';

import yup from '../../utility/yup';
import { selectAuthRoot } from '../../store/modules/auth/selectors';
import { selectInvitation } from '../../store/modules/app/selectors';
import { useActionDispatcher, useTitle } from '../../hooks';
import { logInRequest } from '../../store/modules/auth/actions';
import { LogInPayload } from '../../store/modules/auth/types';
import {
  FormInput,
  FormSubmit,
  Link,
  Space,
  FormSwitch,
  PagesTitle,
  IntercomComponent,
} from '../../components';
import { Links } from '../../utility/links';
import PageContainer from './PageContainer';


const useStyles = makeStyles(({ colors, palette }: Theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    flexGrow: 1,
  },
  subtitle: {
    color: palette.text.primary,
    fontSize: '16px',
    marginTop: '16px',
  },
  control: {
    marginTop: '33px',
  },
  controlLabel: {
    color: palette.text.primary,
    fontSize: '14px',
    fontFamily: 'Roboto',
  },
  submit: {
    height: '40px',
    borderRadius: '5px',
    fontWeight: 600,
    fontFamily: 'Montserrat',
    color: palette.common.white,
    textTransform: 'none',
    width: '100%',
    '& > span:first-child': {
      paddingTop: '2px',
      paddingBottom: '2px',
    },
  },
  forgot: {
    display: 'block',
    fontSize: '14px',
    marginTop: '10px',
  },
}), { name: 'LogIn' });

const loginSchema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().min(7).max(32).required(),
  rememberMe: yup.bool(),
});

const LogIn = () => {
  const classes = useStyles();

  useTitle('Login');

  const { loading } = useSelector(selectAuthRoot);
  const invitation = useSelector(selectInvitation);
  const remember = JSON.parse(localStorage.getItem('auth-remember') || '{}').email;

  const handleAuth = useActionDispatcher(logInRequest);

  const initialValues = useMemo(() => ({
    email: invitation?.email || remember,
    password: '',
    rememberMe: Boolean(remember),
  }), [remember, invitation]);

  return (
    <PageContainer>
      <Grid container className={classes.root}>
        <PagesTitle>Log In</PagesTitle>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Don&apos;t have an account?
          <Space />
          <Link to="/pages/register">
            Sign up.
          </Link>
        </Typography>
        <Grid item xs={12} container>
          <Formik<LogInPayload>
            initialValues={initialValues}
            onSubmit={handleAuth}
            validationSchema={loginSchema}
            validateOnMount
            enableReinitialize
          >
            <Form className={classes.form}>
              <FormInput
                className={classes.control}
                fullWidth
                label="Email Address"
                name="email"
                autoComplete="email"
                dontCheckEmptyValue={false}
                autoFocus
              />
              <FormInput
                className={classes.control}
                fullWidth
                name="password"
                label="Password"
                type="password"
                autoComplete="current-password"
                dontCheckEmptyValue={false}
              />
              <div className={classes.forgot}>
                <Link to="/pages/password-recover">Forgot Password</Link>
              </div>
              <FormSwitch
                label="Remember me"
                name="rememberMe"
                classes={{
                  root: classes.control,
                  label: classes.controlLabel,
                }}
              />
              <FormSubmit
                className={clsx(classes.submit, classes.control)}
                variant="contained"
                color="primary"
                disabled={loading}
              >
                Log In
              </FormSubmit>
            </Form>
          </Formik>
        </Grid>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Questions?
          <Space />
          <Link to={Links.support} defaultAction target="_blank">
            Find Help.
          </Link>
        </Typography>
        <IntercomComponent />
      </Grid>
    </PageContainer>
  );
};

export default LogIn;
